// VideoGrid.jsx               грід відео (мініатюри)

import React, { useRef, useCallback } from 'react';
import PropTypes from 'prop-types';

export default function VideoGrid({
    videos,
    onOpen,
    isSelected,
    onItemClick,
    onItemContextMenu,
    onSwipeLeft,
    onSwipeRight,
}) {
    const lastTapRef = useRef({ time: 0, id: null });
    const touchStartRef = useRef({ x: 0, y: 0 });

    const isTouchDevice = typeof window !== 'undefined'
        && ('ontouchstart' in window || navigator.maxTouchPoints > 0);

    const handleItemClick = useCallback(
        (v, idx, e) => {
            onItemClick?.(v, e);

            if (!isTouchDevice) return;

            const now = Date.now();
            const { time: lastTime, id: lastId } = lastTapRef.current;

            // другий тап по тому самому елементу за 300мс → вважаємо double-tap
            if (lastId === v.id && now - lastTime < 300) {
                onOpen?.(idx, 'video');
            }

            lastTapRef.current = { time: now, id: v.id };
        },
        [isTouchDevice, onItemClick, onOpen]
    );

    const handleTouchStart = useCallback((e) => {
        const touch = e.touches[0];
        touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    }, []);

    const handleTouchEnd = useCallback((e) => {
        const touch = e.changedTouches[0];
        const dx = touch.clientX - touchStartRef.current.x;
        const dy = touch.clientY - touchStartRef.current.y;

        const absDx = Math.abs(dx);
        const absDy = Math.abs(dy);

        // горизонтальний свайп, щоб не спрацьовувати на скролі
        if (absDx > 40 && absDx > absDy) {
            if (dx < 0) {
                onSwipeLeft?.();
            } else {
                onSwipeRight?.();
            }
        }
    }, [onSwipeLeft, onSwipeRight]);

    return (
        <>
            <h3 className="mb-2 font-medium">Відео</h3>
            <ul
                className="grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5"
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
            >
                {videos.map((v, idx) => {
                    const sel = isSelected?.(v);
                    return (
                        <li key={v.id} className="group">
                            <button
                                onClick={(e) => handleItemClick(v, idx, e)}
                                onDoubleClick={() => onOpen?.(idx, 'video')}
                                onContextMenu={(e) => onItemContextMenu?.(v, e)}
                                data-sel-key={`video:${v.id}`}
                                aria-selected={isSelected(v)}
                                className={[
                                    "block w-full aspect-video overflow-hidden rounded-2xl shadow-sm bg-black",
                                    sel ? "ring-2 ring-blue-500 bg-blue-50" : "",
                                ].join(" ")}
                                title={v.name}
                            >
                                {v.thumb ? (
                                    <img
                                        src={v.thumb}
                                        alt={v.name || 'Відео'}
                                        loading="lazy"
                                        className="h-full w-full object-cover opacity-80 group-hover:opacity-100"
                                    />
                                ) : (
                                    <div className="h-full w-full flex items-center justify-center text-white/80">
                                        ▶
                                    </div>
                                )}
                            </button>
                            <div
                                className="mt-1 text-xs truncate text-gray-700"
                                title={v.name}
                            >
                                {v.name}
                            </div>
                        </li>
                    );
                })}
            </ul>
        </>
    );
}

VideoGrid.propTypes = {
    videos: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string,
        thumb: PropTypes.string,
        url: PropTypes.string,
    })),
    onOpen: PropTypes.func.isRequired,
    isSelected: PropTypes.func.isRequired,
    onItemClick: PropTypes.func.isRequired,
    onItemContextMenu: PropTypes.func.isRequired,

    // нові пропси для свайпів
    onSwipeLeft: PropTypes.func,
    onSwipeRight: PropTypes.func,
};
